import { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { com } from '@eidu/web'
import useRegistryAssets from '../../../../io/useRegistryAssets'
import useRegistryAsset from '../../../../io/useRegistryAsset'
import PublishableEntityKinds = com.eidu.sharedlib.entity.publish.PublishableEntityKinds

const KIND_OPTIONS = ['', ...Array.from(PublishableEntityKinds.asJsReadonlySetView()).map((k) => k.toString())]

const formatDate = (ms: number) => new Date(ms).toLocaleString()

type RegistryAssetPickerProps = {
  onSelect: (entityId: string, versionId: string) => void
  selectedEntityId?: string
  selectedVersionId?: string
  kind?: string
}

type PickedAssetDetailProps = {
  entityId: string
  initialVersionId?: string
  onSelect: (entityId: string, versionId: string) => void
}

const PickedAssetDetail = ({ entityId, initialVersionId, onSelect }: PickedAssetDetailProps) => {
  const { detail, isLoading: detailLoading } = useRegistryAsset(entityId)
  const [pickedVersion, setPickedVersion] = useState<string | undefined>(initialVersionId)
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect

  useEffect(() => {
    if (!detail || pickedVersion !== undefined) return
    const latest = detail.versions[0]?.versionId
    if (latest) {
      setPickedVersion(latest)
      onSelectRef.current(detail.entityId, latest)
    }
  }, [detail, pickedVersion])

  const onPickVersion = (versionId: string) => {
    setPickedVersion(versionId)
    if (detail) onSelectRef.current(detail.entityId, versionId)
  }

  if (!detail) return null
  return (
    <>
      <Typography variant="subtitle1">{detail.name}</Typography>
      <Typography variant="caption">Entity {detail.entityId.slice(0, 8)}…</Typography>
      {detailLoading ? (
        <CircularProgress size={20} />
      ) : (
        <TextField
          select
          label="Pin to version"
          size="small"
          value={pickedVersion ?? ''}
          onChange={(e) => onPickVersion(e.target.value)}
          fullWidth
        >
          {detail.versions.map((v) => (
            <MenuItem key={v.versionId} value={v.versionId}>
              {v.versionId.slice(0, 8)}… · {formatDate(v.createdAt)}
            </MenuItem>
          ))}
        </TextField>
      )}
    </>
  )
}

const RegistryAssetPicker = ({
  onSelect,
  selectedEntityId,
  selectedVersionId,
  kind = undefined,
}: RegistryAssetPickerProps) => {
  const [search, setSearch] = useState('')
  const [selectedKind, setSelectedKind] = useState('')
  const [pickedEntityId, setPickedEntityId] = useState<string | undefined>(selectedEntityId)
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const selectedRowRef = useRef<HTMLDivElement | null>(null)
  const didScrollToSelectedRef = useRef(false)

  const effectiveKind = kind ?? (selectedKind || undefined)
  const { assets, loadMore, hasMore, loading, error } = useRegistryAssets({
    search: search.trim() || undefined,
    kind: effectiveKind,
  })

  useEffect(() => {
    const node = sentinelRef.current
    if (!node) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loading) loadMore()
      },
      { rootMargin: '256px' }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore, loading, loadMore])

  const selectedInList = !!selectedEntityId && assets.some((a) => a.entityId === selectedEntityId)
  useEffect(() => {
    if (didScrollToSelectedRef.current || !selectedInList) return
    selectedRowRef.current?.scrollIntoView({ block: 'center' })
    didScrollToSelectedRef.current = true
  }, [selectedInList])
  useEffect(() => {
    if (selectedEntityId && !selectedInList && hasMore && !loading) loadMore()
  }, [selectedEntityId, selectedInList, hasMore, loading, loadMore])

  return (
    <Stack direction="row" spacing={2} sx={{ height: '100%' }}>
      <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1}>
          <TextField
            label="Search"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1 }}
          />
          {kind === undefined && (
            <TextField
              select
              label="Kind"
              size="small"
              value={selectedKind}
              onChange={(e) => setSelectedKind(e.target.value)}
              sx={{ width: 140 }}
            >
              {KIND_OPTIONS.map((k) => (
                <MenuItem key={k} value={k}>
                  {k || 'Any'}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
        {error && <Alert severity="error">{error.message}</Alert>}
        <Box sx={{ flex: 1, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <List dense>
            {assets.map((a) => (
              <ListItemButton
                key={a.entityId}
                ref={selectedEntityId === a.entityId ? selectedRowRef : undefined}
                selected={selectedEntityId === a.entityId}
                onClick={() => setPickedEntityId(a.entityId)}
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">{a.name}</Typography>
                      <Chip label={a.kind} size="small" />
                    </Stack>
                  }
                  secondary={`Org ${a.publishingOrgId.slice(0, 8)}…`}
                />
              </ListItemButton>
            ))}
            <div ref={sentinelRef} />
            {loading && (
              <ListItem>
                <CircularProgress size={20} />
              </ListItem>
            )}
            {!loading && assets.length === 0 && (
              <ListItem>
                <ListItemText secondary="No published assets." />
              </ListItem>
            )}
          </List>
        </Box>
      </Stack>
      <Stack spacing={1} sx={{ width: 280 }}>
        {pickedEntityId ? (
          <PickedAssetDetail
            key={pickedEntityId}
            entityId={pickedEntityId}
            initialVersionId={pickedEntityId === selectedEntityId ? selectedVersionId : undefined}
            onSelect={onSelect}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Pick an asset to choose a version.
          </Typography>
        )}
      </Stack>
    </Stack>
  )
}

export default RegistryAssetPicker
